// Suggested tasks (spec 07 §6): bullets under "Next steps" / "Prochaines étapes" / "Action items" / "Suivis".
// Suggestions only: nothing is created without the user clicking.
// Linear-time: headings are parsed without backtracking and lines over MAX_LINE_REGEX_CHARS are never matched.
import { splitUnderscoreEmphasis } from "./markdownBlocks.ts";
import { MAX_LINE_REGEX_CHARS } from "./sanitize.ts";

export const MAX_ACTION_ITEMS = 20;
export const MAX_ACTION_ITEM_CHARS = 300;

const ACTION_HEADINGS = new Set([
  "next steps",
  "next step",
  "action items",
  "action item",
  "follow ups",
  "follow-ups",
  "followups",
  "to do",
  "todo",
  "prochaines etapes",
  "prochaine etape",
  "suivis",
  "suivi",
  "actions a prendre",
  "a faire",
  "points d action",
]);
const COMBINING = new RegExp(
  `[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`,
  "g",
);
const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(COMBINING, "")
    .toLowerCase()
    .replace(/[’']/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Removes trailing characters found in `chars` (manual loop: no backtracking). */
function trimEndChars(s: string, chars: string): string {
  let end = s.length;
  while (end > 0 && chars.includes(s[end - 1])) end--;
  return s.slice(0, end);
}

const SPACES = " \t\r\n";

function headingText(line: string): string | null {
  if (line.length > MAX_LINE_REGEX_CHARS) return null;
  if (line.startsWith("#")) {
    const m = /^#{1,6}[ \t]+/.exec(line);
    if (!m) return null;
    const rest = trimEndChars(line.slice(m[0].length), `${SPACES}#`).trim();
    return rest || null;
  }
  const t = line.trim();
  if (t.startsWith("**") || t.startsWith("__")) {
    const body = trimEndChars(t, `${SPACES}:`);
    if (body.length > 4 && (body.endsWith("**") || body.endsWith("__")))
      return body.slice(2, -2);
    return null;
  }
  const colon = /^([^\-*+•\d\s][^:]{0,40}?)\s*:$/.exec(t);
  return colon ? colon[1] : null;
}

export const isActionHeading = (text: string): boolean =>
  ACTION_HEADINGS.has(trimEndChars(fold(text), `${SPACES}:.`));

const BULLET = /^\s*(?:[-*+•]|\d{1,3}[.)])\s+(?:\[[ xX]\]\s+)?(.+)$/;
const cleanInline = (s: string) =>
  s
    .replace(/\[([^\]]*)\]\((?:[^()]|\([^()]*\))*\)/g, "$1")
    .replace(/(\*\*|`)/g, "")
    .split("\n")
    .map((l) =>
      splitUnderscoreEmphasis(l)
        .map((x) => x.text)
        .join(""),
    )
    .join("\n")
    .replace(/\s+/g, " ")
    .trim();

export function parseActionItems(markdown: string): string[] {
  const items: string[] = [];
  const seen = new Set<string>();
  let inSection = false;
  for (const line of markdown.split("\n")) {
    if (line.length > MAX_LINE_REGEX_CHARS) continue;
    const h = headingText(line);
    if (h !== null) {
      inSection = isActionHeading(h);
      continue;
    }
    if (!inSection) continue;
    const b = BULLET.exec(line);
    if (!b) continue;
    const text = cleanInline(b[1]).slice(0, MAX_ACTION_ITEM_CHARS);
    const key = fold(text);
    if (!text || seen.has(key)) continue;
    seen.add(key);
    items.push(text);
    if (items.length >= MAX_ACTION_ITEMS) break;
  }
  return items;
}
